import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Printer, Lock, Unlock, History, Settings2, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import type { ProductionMonthLock, ProductionOrder } from '@/services/productionOrders'

export interface ProductionMonthBarProps {
  selectedMonth: string
  months: ProductionMonthLock[]
  orders: ProductionOrder[]
  currentLock: ProductionMonthLock | null
  canWrite: boolean
  canApprove: boolean
  onSelectMonth: (month: string) => void
  onApproveMonth: () => void
  onOpenReopenModal: () => void
  onOpenHistoryModal: () => void
  onOpenWorkUnitsModal: () => void
}

export function ProductionMonthBar({
  selectedMonth,
  months,
  orders,
  currentLock,
  canWrite,
  canApprove,
  onSelectMonth,
  onApproveMonth,
  onOpenReopenModal,
  onOpenHistoryModal,
  onOpenWorkUnitsModal,
}: ProductionMonthBarProps): React.ReactElement {
  const navigate = useNavigate()

  // KPIs
  const totalPlannedQty = orders.reduce((sum, o) => sum + (Number(o.planned_qty) || 0), 0)
  const lineCount = orders.length
  const lateOrRiskCount = orders.filter(
    (o) => o.status === 'late' || o.status === 'at_risk',
  ).length

  const isApproved = currentLock?.status === 'approved'

  // Build unique month list (unscheduled lives in "Nhận từ Sale" tab — not here)
  const monthOptions = Array.from(
    new Set([
      ...months.map((m) => m.month),
      // Ensure current and next few months exist as fallback if list is empty
      new Date().toISOString().slice(0, 7),
    ]),
  )
    .filter((m) => m !== 'unscheduled')
    .sort()

  return (
    <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      {/* Top Toolbar: Month selector + Status + Action Buttons */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Left: Month selector & status */}
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-sm font-bold text-slate-700">Tháng sản xuất:</label>
          <select
            className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-800 outline-none focus:border-emerald-600 shadow-sm"
            value={selectedMonth === 'unscheduled' ? monthOptions[0] ?? selectedMonth : selectedMonth}
            onChange={(e) => onSelectMonth(e.target.value)}
          >
            {monthOptions.map((m) => (
              <option key={m} value={m}>
                Tháng {m}
              </option>
            ))}
          </select>

          {/* Month Status Badge */}
          {isApproved ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-800 border border-emerald-300 shadow-sm">
              <Lock className="h-3.5 w-3.5" /> Đã duyệt (khóa)
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-3 py-1 text-xs font-bold text-amber-800 border border-amber-300">
              <Unlock className="h-3.5 w-3.5" /> Kế hoạch Nháp
            </span>
          )}

          {currentLock && currentLock.reopened_count > 0 && (
            <span className="text-xs font-medium text-amber-700 italic">
              (Đã mở lại {currentLock.reopened_count} lần)
            </span>
          )}
        </div>

        {/* Right: Actions */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Work Units Catalog Modal button */}
          {canWrite && (
            <Button
              size="sm"
              variant="secondary"
              type="button"
              onClick={onOpenWorkUnitsModal}
              title="Quản lý danh mục Đơn vị sản xuất (mộc, sơn, gia công)"
            >
              <Settings2 className="mr-1.5 h-4 w-4 text-slate-600" />
              Đơn vị sản xuất
            </Button>
          )}

          {/* Month History button */}
          <Button
            size="sm"
            variant="secondary"
            type="button"
            onClick={onOpenHistoryModal}
            title="Xem nhật ký phê duyệt & thay đổi của tháng"
          >
            <History className="mr-1.5 h-4 w-4 text-slate-600" />
            Lịch sử
          </Button>

          {/* Approve / Reopen buttons */}
          {isApproved ? (
            canApprove && (
              <Button
                size="sm"
                variant="secondary"
                type="button"
                onClick={onOpenReopenModal}
                title="Mở lại kế hoạch tháng để điều chỉnh mốc ngày"
              >
                <Unlock className="mr-1.5 h-4 w-4 text-amber-700" />
                Mở lại tháng
              </Button>
            )
          ) : (
            canApprove && (
              <Button
                size="sm"
                variant="primary"
                type="button"
                onClick={onApproveMonth}
                title="Duyệt khóa kế hoạch tháng và lưu mốc baseline"
              >
                <CheckCircle2 className="mr-1.5 h-4 w-4" />
                Duyệt tháng
              </Button>
            )
          )}

          {/* Print button */}
          <Button
            size="sm"
            variant="secondary"
            type="button"
            onClick={() => navigate(`/planning/production-orders/print?month=${selectedMonth}`)}
            title="Mở trang in Lệnh sản xuất"
          >
            <Printer className="mr-1.5 h-4 w-4 text-slate-700" />
            In lệnh sản xuất
          </Button>
        </div>
      </div>

      {/* KPI Cards: Tổng SL kế hoạch, Số dòng, Nguy cơ trễ / Trễ */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 pt-2 border-t border-slate-100">
        <div className="rounded-md border border-slate-200 bg-slate-50/70 p-3">
          <p className="text-xs font-medium text-slate-500">Tổng SL kế hoạch</p>
          <p className="mt-1 text-xl font-bold text-slate-900 font-mono">
            {totalPlannedQty.toLocaleString('vi-VN')} <span className="text-xs font-normal text-slate-500">sp</span>
          </p>
        </div>

        <div className="rounded-md border border-slate-200 bg-slate-50/70 p-3">
          <p className="text-xs font-medium text-slate-500">Số dòng sản xuất</p>
          <p className="mt-1 text-xl font-bold text-slate-900 font-mono">
            {lineCount} <span className="text-xs font-normal text-slate-500">dòng</span>
          </p>
        </div>

        <div className={`rounded-md border p-3 ${
          lateOrRiskCount > 0
            ? 'border-rose-300 bg-rose-50/60'
            : 'border-slate-200 bg-slate-50/70'
        }`}>
          <p className="text-xs font-medium text-slate-500">Nguy cơ trễ / Trễ</p>
          <p className={`mt-1 text-xl font-bold font-mono ${
            lateOrRiskCount > 0 ? 'text-rose-700' : 'text-slate-800'
          }`}>
            {lateOrRiskCount} <span className="text-xs font-normal text-slate-500">dòng</span>
          </p>
        </div>
      </div>
    </div>
  )
}
